import React, { useState } from 'react';
import { ClientEntity } from '../types';
import { TrendingUp, AlertCircle, TrendingDown, Target, Play, Image as ImageIcon } from 'lucide-react';

export const CreativeLibrary: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const [activeStatus, setActiveStatus] = useState<'vencedores' | 'teste' | 'perdedores'>('vencedores');

  const mockCreatives = [
    {
      id: '1',
      title: 'Vídeo Depoimento - Paciente Satisfeito',
      format: 'vídeo',
      status: 'vencedores',
      img: 'https://images.unsplash.com/photo-1551076805-e1869043e560?auto=format&fit=crop&q=80&w=400&h=300',
      ctr: '3.2%',
      cpa: 'R$ 18,50',
      spend: 'R$ 1.250',
    },
    {
      id: '2',
      title: 'Carrossel - Antes e Depois (Tratamento)',
      format: 'imagem',
      status: 'vencedores',
      img: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=400&h=300',
      ctr: '2.8%',
      cpa: 'R$ 21,00',
      spend: 'R$ 890',
    },
    {
      id: '3',
      title: 'Reels - Tour pela Clínica',
      format: 'vídeo',
      status: 'teste',
      img: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=400&h=300',
      ctr: '1.5%',
      cpa: 'R$ 35,00',
      spend: 'R$ 150',
    },
    {
      id: '4',
      title: 'Imagem Estática - Promoção Mês das Mães',
      format: 'imagem',
      status: 'perdedores',
      img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=400&h=300',
      ctr: '0.8%',
      cpa: 'R$ 85,00',
      spend: 'R$ 300',
    }
  ];

  const statuses = [
    { id: 'vencedores', label: 'Vencedores', icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
    { id: 'teste', label: 'Em Teste', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { id: 'perdedores', label: 'Perdedores', icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' }
  ];

  const filteredCreatives = mockCreatives.filter(c => c.status === activeStatus);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2 tracking-tight">
          Biblioteca de Criativos
        </h2>
        <p className="text-gray-500 mb-8">
          Acompanhe os anúncios criados e organizados pela inteligência de acordo com a performance.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {statuses.map(status => {
             const Icon = status.icon;
             const isActive = activeStatus === status.id;
             const count = mockCreatives.filter(c => c.status === status.id).length;
             return (
               <button
                 key={status.id}
                 onClick={() => setActiveStatus(status.id as any)}
                 className={`flex-1 p-4 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${
                   isActive 
                     ? `${status.bg} ${status.border} shadow-sm ring-1 ring-inset ${status.color.replace('text-', 'ring-')}` 
                     : 'border-gray-200 bg-white hover:bg-gray-50'
                 }`}
               >
                 <Icon size={24} className={isActive ? status.color : 'text-gray-400'} />
                 <div className="text-center">
                   <div className={`font-semibold ${isActive ? status.color : 'text-gray-700'}`}>{status.label}</div>
                   <div className={`text-xs ${isActive ? status.color : 'text-gray-500'}`}>{count} anúncios</div>
                 </div>
               </button>
             );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCreatives.length > 0 ? (
            filteredCreatives.map(creative => (
              <div key={creative.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm group hover:shadow-md transition-all">
                <div className="h-48 bg-gray-100 relative overflow-hidden">
                  <img src={creative.img} alt={creative.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-gray-900 text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                    {creative.format === 'vídeo' ? <Play size={10} /> : <ImageIcon size={10} />}
                    {creative.format.toUpperCase()}
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="font-semibold text-gray-900 mb-4 line-clamp-2" title={creative.title}>{creative.title}</h4>
                  
                  <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">CTR</p>
                      <p className="text-sm font-semibold text-gray-900">{creative.ctr}</p>
                    </div>
                    <div className="text-center border-l border-r border-gray-200">
                      <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">CPA</p>
                      <p className="text-sm font-semibold text-gray-900">{creative.cpa}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Gasto</p>
                      <p className="text-sm font-semibold text-gray-600">{creative.spend}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-12 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
               <p className="text-gray-500">Nenhum criativo encontrado nesta categoria.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
